import { UserService } from './../../services/user.service';
import { Component, OnDestroy, OnInit, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentfulService } from '../../services/contentful.service';
import { Subscription, filter } from 'rxjs';
import { BreadcrumbDataService } from 'src/app/services/breadcrumb-data.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MetadataService } from 'src/app/services/metadata.service';
import { PortfoliosMeta } from 'src/app/models/classes/portfolios-meta.class';
import { InvestorMeta } from 'src/app/models/classes/investor-meta.class';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { AutoAdjustingDropdownComponent } from 'src/app/auto-adjusting-dropdown/auto-adjusting-dropdown.component';
import { MatIconModule } from '@angular/material/icon';
import { InvestorsStorageService } from 'src/app/services/investors-storage.service';

@Component({
    selector: 'app-investor-dropdown',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatInputModule,
        AutoAdjustingDropdownComponent,
        MatIconModule
    ],
    templateUrl: './investor-dropdown.component.html',
    styleUrls: ['./investor-dropdown.component.scss']
})
export class InvestorDropdownComponent implements OnInit, OnDestroy {
  router = inject(Router);
  private contentfulService = inject(ContentfulService);
  private userService = inject(UserService);
  private activeRoute = inject(ActivatedRoute);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private metadataService = inject(MetadataService);
  private activityTracking = inject(ActivityTrackingService);
  private investorsStorage = inject(InvestorsStorageService);

  readonly updateWidth = input<boolean>(false);
  readonly iconSet = toSignal(
    this.contentfulService.getIconSetValue().pipe(filter((v): v is Record<string, string> => !!v)),
    { initialValue: {} as Record<string, string> }
  );
  readonly selectedInvestorId = input<number | undefined>(undefined);

  private readonly syncInvestorName = effect(() => {
    this.getInvestorName(this.selectedInvestorId());
  });
  investorsDropdownData = signal<InvestorMeta[]>([]);
  $investors!: Subscription;
  private $searchInput!: Subscription;
  investorName = signal<string>('');
  readonly showInvestorsDropList = computed(() => this.investorsDropdownData().length > 1);
  searchInput = new FormControl<PortfoliosMeta | string>('');
  filteredOptions = signal<PortfoliosMeta[]>([]);
  portfolioData: PortfoliosMeta[] = [];
  filteredPortfolioData: PortfoliosMeta[] = [];
  readonly autocomplete = viewChild<MatAutocomplete>('auto');
  filteredInvestorDropdownData = signal<InvestorMeta[]>([]);
  noMatchFilterError = false;
  externalUser = true;

  ngOnInit(): void {
    this.externalUser = this.userService.isExternalUser();
    this.$investors = (this.investorsStorage.getInvestors() as import('rxjs').Observable<InvestorMeta[]>).subscribe((res: InvestorMeta[]) => {
      if (!res || res.length == 0) return;
      this.investorsDropdownData.set(res);
      this.filteredInvestorDropdownData.set(res);
      this.getInvestorName(this.selectedInvestorId());
      if (!this.externalUser) {
        this.metadataService.fetchPortfolioMetadata().then((metadata: PortfoliosMeta[]) => {
          this.portfolioData = metadata;
          this.filteredOptions.set(this._filter(''));
          this.$searchInput = this.searchInput.valueChanges.subscribe(value => {
            this.filteredOptions.set(this._filter(value || ''));
          });
        })
      }
    });
  }

  ngOnDestroy(): void {
    this.$investors.unsubscribe();
    this.$searchInput?.unsubscribe();
  }

  private _filter(value: PortfoliosMeta | string): PortfoliosMeta[] {
    let filterValue: string;
    if (typeof value === 'object') {
      filterValue = value.ticker ? `${value.ticker} - ${value.name}`.toLowerCase() : (value.name || '').toLowerCase();
    } else {
      filterValue = (value as string).toLowerCase();
    }
    this.filteredPortfolioData = this.portfolioData
      .filter(p => {
        const combined = `${p.ticker} - ${p.name}`.toLowerCase();
        return p.ticker?.toLowerCase().includes(filterValue) ||
               p.name.toLowerCase().includes(filterValue) ||
               combined.includes(filterValue);
      })
      .sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
    if (this.filteredPortfolioData.length > 0) {
      this.noMatchFilterError = false;
    }
    else {
      this.noMatchFilterError = true;
    }
    return this.filteredPortfolioData;
  }

  filterSelectedPortfolio() {
    const searchValue = this.searchInput.value;
    if (!searchValue) return;
    let filteredPortfolioInvestorIds: number[] = [];
    if (typeof searchValue === 'object' && searchValue.relatedInvestors) {
      filteredPortfolioInvestorIds = searchValue.relatedInvestors;
    } else {
      filteredPortfolioInvestorIds = this.filteredPortfolioData.map(pd => pd.relatedInvestors)[0];
    }
    const filtered = this.investorsDropdownData().filter(inv => filteredPortfolioInvestorIds.includes(inv.id));
    this.filteredInvestorDropdownData.set(filtered);
    const selectedInvestorId = filtered[0].id;
    this.selectActiveInvestor(selectedInvestorId);
    if (selectedInvestorId) {
      this.activityTracking.useFilterTool("portfolio filter", filtered[0])
    }
  }

  displayFn(option: PortfoliosMeta | null): string {
    return option && option.ticker ? `${option.ticker} - ${option.name}` : (option?.name || '');
  }

  resetFilter() {
    if (this.searchInput.value && this.searchInput.value !== '') {
      this.searchInput.reset('');
      this.autocomplete()?.options.forEach((item) => {
        item.deselect()
      });
      this.filteredInvestorDropdownData.set([...this.investorsDropdownData()]);
    }
  }

  selectActiveInvestor(id: number): void {
    this.investorsStorage.setActiveInvestor(id);
    this.getInvestorName(id);
    if (this.userService.userIsOnInvestorDashboard()) {
      const reportingPeriod = this.breadcrumbDataService.getReportingPeriodForSelectedInvestor(id);
      this.router.navigate(
        ['/investor/' + id], {
        relativeTo: this.activeRoute,
        queryParams: { reportingdate: reportingPeriod },
        queryParamsHandling: 'merge'
      });
    } else {
      this.breadcrumbDataService.redirectToDashboard(id)
    }
  }

  getInvestorName(id: number | undefined) {
    if (id) {
      const selectedInvestor = this.investorsDropdownData().filter(investor => investor.id === Number(id))[0];
      if (selectedInvestor?.name && selectedInvestor?.name?.length < 100) {
        this.investorName.set(selectedInvestor?.name);
      } else {
        this.investorName.set(selectedInvestor?.name?.substring(0, 100) + "...");
      }
    }
  }
}
